import { Component, Input } from '@angular/core';
import {
  getAccessibleBadgeStyles,
  getBackgroundColor,
  getReadableTextOnWhite,
} from 'src/app/utils/utils.function';

/**
 * Shared status pill — the list-grid status badge used across the app.
 *
 * Exists because the contrast rule kept being re-derived (or forgotten) at each
 * call site: ~60 templates painted a raw `status.color` straight onto the label,
 * so pale statuses rendered light-on-light. The Bids list solved it with a local
 * `getStatusTextColor()` helper; that logic now lives here so every module gets
 * it for free and new call sites cannot regress.
 *
 * Deliberately carries no SCSS: `.btn_status_container`, `.status_circle`,
 * `.status-label` and `.status-lock-icon` are global (styles.scss:4372), so a
 * pill renders identically wherever it is dropped.
 */
@Component({
  selector: 'app-status-pill',
  templateUrl: './status-pill.component.html',
  standalone: false,
})
export class StatusPillComponent {
  /** Status colour from the API (`status.color`). */
  @Input() color: string | null = null;

  /** Status name (`status.name`). */
  @Input() label: string | null = null;

  /** True when `status.lock_status == 1`; renders the padlock. */
  @Input() locked = false;

  /** Character budget for the label. Defaults to the list convention below. */
  @Input() truncateAt: number | null = null;

  @Input() lockTooltip = 'Status Locked';

  /**
   * `tint`   — pale fill, coloured border and dot. The list-grid convention, and
   *            the default so the 17 list call sites are untouched.
   * `solid`  — fill in the status colour with contrast-checked text and no dot,
   *            matching the header badge on the detail pages
   *            (`.status-badge-pill`, e.g. detail-proposal.component.html:47).
   */
  @Input() variant: 'tint' | 'solid' = 'tint';

  get isSolid(): boolean {
    return this.variant === 'solid';
  }

  /** Border matches the fill, as on the detail header. */
  get borderColor(): string {
    return this.isSolid
      ? this.solidStyles.backgroundColor
      : this.color || 'transparent';
  }

  /**
   * Colourless statuses fall back to the same orange the detail-page header uses
   * (`detail-proposal.component.html:49`), so a pill and the header badge agree
   * even when the API sends no colour.
   */
  private static readonly SOLID_FALLBACK = '#f58631';

  /**
   * getAccessibleBadgeStyles() returns a MATCHED pair: a background darkened until a
   * black/white label can clear 4.5:1, and the label colour chosen for that darkened
   * fill. Both must come from the same call — previously `background` painted the raw
   * status colour while `textColor` took the contrast-checked label, so the label was
   * picked for a darker fill than the one rendered (e.g. #111827 on #FF2B2B ≈ 4.3:1).
   */
  private get solidStyles() {
    return getAccessibleBadgeStyles(this.color || StatusPillComponent.SOLID_FALLBACK);
  }

  /** Pill fill: the contrast-checked status colour when solid, otherwise the pale tint. */
  get background(): string {
    return this.isSolid
      ? this.solidStyles.backgroundColor
      : getBackgroundColor(this.color || '');
  }

  /**
   * Tint: the status colour darkened until it clears 4.5:1 against the white
   * pill. Solid: the same contrast rule the detail header uses via the
   * `statusTextColor` pipe, so light fills get dark text and vice versa.
   */
  get textColor(): string {
    return this.isSolid
      ? this.solidStyles.color
      : getReadableTextOnWhite(this.color || '');
  }

  /** Locked pills lose two characters to the padlock, matching the lists. */
  get truncateLength(): number {
    return this.truncateAt ?? (this.locked ? 13 : 15);
  }
}
